import { Component } from '@theme/component';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * <admira-brand-hub> — the brand page's behaviour (sections/admira-brand-hub.liquid).
 *
 * The cards and dialogs work without it: each card is a `<dialog-component>`
 * whose button opens its own server-rendered dialog. This module adds what
 * the Keystone subpages' URLs used to give:
 *
 *   - **Deep links.** `?sistem=<handle>` opens that system's dialog on load —
 *     the 301s from the retired subpages land on it — and opening or closing a
 *     dialog keeps the parameter in step (`replaceState`, so Back still leaves
 *     the page rather than closing a dialog).
 *   - **Category tabs.** Toggle buttons (`aria-pressed`) that hide the cards
 *     outside the category; `?categorie=` preselects one. With no JavaScript
 *     every card shows, which is the right fallback for a filter.
 *   - **"Adaugă toate în coș".** One request for the system's products, through
 *     the calculators' `addToCart` (Horizon's batch add, so the drawer and the
 *     count update the standard way).
 *   - **"Solicită ofertă".** Closes the dialog, scrolls to the page's contact
 *     form, picks the price-quote subject and starts the message with the
 *     system's name.
 *
 * @extends {Component<{ tabs?: HTMLButtonElement[], items?: HTMLElement[], dialogs?: HTMLElement[] }>}
 */
class AdmiraBrandHub extends Component {
  /** @type {typeof import('./admira-calculator-core.js') | null} */
  #core = null;

  connectedCallback() {
    super.connectedCallback();

    for (const tab of this.refs.tabs ?? []) {
      tab.addEventListener('click', () => this.#filter(tab.dataset.categorie ?? ''));
    }

    for (const host of this.refs.dialogs ?? []) {
      host.addEventListener(DialogOpenEvent.eventName, () => this.#setParam('sistem', host.dataset.sistem ?? ''));
      host.addEventListener(DialogCloseEvent.eventName, () => this.#setParam('sistem', ''));
    }

    this.addEventListener('click', this.#handleClick);

    const params = new URLSearchParams(window.location.search);
    const categorie = params.get('categorie');
    if (categorie) this.#filter(categorie);

    const sistem = params.get('sistem') || window.location.hash.replace(/^#sistem-/, '');
    if (sistem) this.#open(sistem);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {string} handle */
  #open(handle) {
    const host = (this.refs.dialogs ?? []).find((element) => element.dataset.sistem === handle);
    if (!host) return;
    // dialog.js may upgrade the hosts after this module runs.
    customElements.whenDefined('dialog-component').then(() => /** @type {any} */ (host).showDialog?.());
  }

  /** @param {string} categorie */
  #filter(categorie) {
    const tabs = this.refs.tabs ?? [];
    if (!tabs.some((tab) => (tab.dataset.categorie ?? '') === categorie)) categorie = '';

    for (const tab of tabs) tab.setAttribute('aria-pressed', String((tab.dataset.categorie ?? '') === categorie));
    for (const item of this.refs.items ?? []) item.hidden = Boolean(categorie) && item.dataset.categorie !== categorie;

    this.#setParam('categorie', categorie);
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  #setParam(key, value) {
    const url = new URL(window.location.href);
    if (value) url.searchParams.set(key, value);
    else url.searchParams.delete(key);
    if (url.hash.startsWith('#sistem-')) url.hash = '';
    window.history.replaceState(window.history.state, '', url);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const add = target?.closest('[data-add-system]');
    if (add instanceof HTMLButtonElement) {
      this.#addSystem(add);
      return;
    }
    const quote = target?.closest('[data-quote]');
    if (quote instanceof HTMLElement) this.#quote(quote);
  };

  /** @param {HTMLButtonElement} button */
  async #addSystem(button) {
    const band = button.closest('.admira-system__products');
    const status = band?.querySelector('.admira-system__products-status');
    const items = [...(band?.querySelectorAll('[data-variant-id]') ?? [])].map((row) => ({
      variantId: /** @type {HTMLElement} */ (row).dataset.variantId ?? '',
      quantity: 1,
    }));
    if (!items.length) return;

    button.disabled = true;
    try {
      this.#core ??= await import(/* @vite-ignore */ this.dataset.core ?? '');
      const result = await this.#core.addToCart(button, items);
      if (status) status.textContent = result.ok ? button.dataset.added ?? '' : result.message || button.dataset.failed || '';
    } finally {
      button.disabled = false;
    }
  }

  /** @param {HTMLElement} trigger */
  async #quote(trigger) {
    const host = trigger.closest('dialog-component');
    if (host && 'closeDialog' in host) await /** @type {any} */ (host).closeDialog();

    const form = document.querySelector('.admira-contact-form form');
    if (!(form instanceof HTMLFormElement)) return;

    const subject = form.querySelector('select[id$="-subject"]');
    if (subject instanceof HTMLSelectElement) {
      const option = [...subject.options].find((o) => /ofert/i.test(o.value));
      if (option) subject.value = option.value;
    }

    const body = form.querySelector('textarea');
    const line = `${trigger.dataset.quote ?? ''}: `;
    if (body instanceof HTMLTextAreaElement && !body.value.includes(line.trim())) {
      body.value = body.value ? `${line}\n${body.value}` : line;
    }

    form.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const first = form.querySelector('input:not([type="hidden"])');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }
}

if (!customElements.get('admira-brand-hub')) customElements.define('admira-brand-hub', AdmiraBrandHub);
